import {useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {snowplowTrackEvent} from "utils/snowplow";
import * as reduxSelectors from "../store/application.reducers";
import {
    copyRuleWithName, deleteRule,
} from "components/workflows/workflows.action";
import translate from "components/translations/translations.wrapper";
import {WorkflowActionsDropdownProps} from "types/workflowActionsDropdown";
import UserInputArea from "components/workflows/UserInputArea.component";
import UserCommentArea from "components/workflows/UserCommentArea.component";
import {useNavigate} from "react-router-dom";
import {PATH_TO_WORKFLOW_DETAILS} from "components/router/paths";
import {DropdownButton} from "components/common/dropdown/DropdownButton.component";
import {IDropdownListItem} from "types/gaiaComponents/dropdownButton";
import { MODAL_ACTIONS, MODAL_SEVERITY } from "utils/constants";
import { useModalContext } from "components/modals/ModalContext";
import { Tooltip } from "components/tooltip/Tooltip";
import { SHARED_TOOLTIP_ID } from "components/tooltip/AppTooltip.component";

function WorkflowsActionsDropdownComponent(props: WorkflowActionsDropdownProps) {
    const dispatch = useDispatch();
    const userConfiguration = useSelector(reduxSelectors.getUsersData);
    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const deleteUserInputRef = useRef(null);
    const copyUserInputRef = useRef(null);
    const navigate = useNavigate();

    const modalContext = useModalContext();
    const toggleModal = modalContext?.toggleModal;

    const navigateToWorkflowDetails = (idRuleConfiguration: number) => {
        navigate(PATH_TO_WORKFLOW_DETAILS + idRuleConfiguration);
    }

    const onCopyRule = function () {
        const newRuleName = copyUserInputRef.current ? (copyUserInputRef.current as HTMLTextAreaElement).value : "";
        let newRule = Object.assign({}, props.rule);
        newRule.name = newRuleName;
        dispatch(copyRuleWithName(companyId, newRule, navigateToWorkflowDetails, props.copyCallback));
    };

    const onDeleteRule = function () {
        const userComment = deleteUserInputRef.current ? (deleteUserInputRef.current as HTMLTextAreaElement).value : "";
        dispatch(deleteRule(companyId, props.rule.idRuleConfiguration, userComment, props.deleteCallback));
    };

    const onShowRuleHistory = function () {
        snowplowTrackEvent({
            category: "audit trail",
            action: "open_workflow_history",
            label: "rule level workflow history",
            value: props.rule.idRuleConfiguration + "",
        }, userConfiguration);

        toggleModal?.(MODAL_ACTIONS.WORKFLOW_HISTORY, {
            ruleId: props.rule.idRuleConfiguration,
        });
    };

    const openCopyConfirm = () => {
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: props.translate("workflowDetails.confirmDialog.copyTitle", props.rule.name),
            description: props.translate("workflowDetails.confirmDialog.copyMessage"),
            severity: MODAL_SEVERITY.INFORMATION,
            confirmButtonText: "popUp.duplicate",
            cancelButtonText: "workflowDetails.confirmDialog.cancel",
            handleAction: () => {
                toggleModal(null);
                onCopyRule();
            },
            moreContent: (
                <UserInputArea
                    inputMessage={props.translate("workflowDetails.confirmDialog.copyInputMessage")}
                    defaultValue={props.rule.name + " (1)"}
                    userInput={copyUserInputRef}
                />
            ),
        });
    };

    const openDeleteConfirm = () => {
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: props.translate("workflowDetails.confirmDialog.deleteTitle", props.rule.name),
            description: props.translate("workflowDetails.confirmDialog.deleteWorkflowMessage"),
            severity: MODAL_SEVERITY.WARNING,
            confirmButtonColor: "btn-default",
            confirmButtonText: "popUp.delete",
            cancelButtonText: "popUp.cancel",
            handleAction: () => {
                toggleModal(null);
                onDeleteRule();
            },
            moreContent: (
                <UserCommentArea
                    inputMessage={props.translate("workflowDetails.confirmDialog.deleteInputMessage")}
                    moreInfo={props.translate("workflowDetails.confirmDialog.moreInfo")}
                    userInput={deleteUserInputRef}
                />
            ),
        });
    };

    const dropdownItems: IDropdownListItem[] = [
        {
            key: "duplicate",
            text: props.translate("workflows.duplicate"),
            onClick: openCopyConfirm,
            disabled: false,
            dataCy: "workflows.duplicate"
        },
        {
            key: "history",
            text: props.translate("workflows.workflowHistoryBtn"),
            onClick: onShowRuleHistory,
            disabled: false,
            dataCy: "workflows.workflowHistoryBtn"
        },
        {
            key: "delete",
            text: props.translate("workflows.delete"),
            onClick: openDeleteConfirm,
            disabled:  props.rule?.readonly,
            dataCy: "workflows.delete"
        }
    ];

    if (!props.rule) {
        return null;
    }

    return (
        <div className="workflow-actions">
            <span data-for={SHARED_TOOLTIP_ID} data-tip={`workflow-actions-tooltip-${props.rule.idRuleConfiguration}`}>
                <DropdownButton
                    id={`workflow-actions-dropdown-${props.rule.idRuleConfiguration}`}
                    type={props.type}
                    icon={props.icon}
                    position={props.position}
                    text={props.icon ? null : props.translate("workflows.moreActions")}
                    items={dropdownItems}
                    isEmpty={false}
                />
            </span>
            <Tooltip
                id={`workflow-actions-tooltip-${props.rule.idRuleConfiguration}`}
                content={() => props.translate("workflows.moreActions")}
            />
        </div>
    );
}

const withTranslations = translate(WorkflowsActionsDropdownComponent);
export default withTranslations;